import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as authActionsV2 from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.actions";
import * as authSelectors from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.selectors";
import api, {
  MCP_SOURCE_HEADERS,
} from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Button,
  DisplayText,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import {
  StyledAuthBody,
  StyledAuthContent,
  StyledAuthFrame,
  StyledAuthLogo,
  StyledAuthMain,
  StyledAuthMedia,
  StyledAuthMediaWrap,
} from "@rentiohq/web-shared/dist/scenes/Auth/Auth.styled";
import { setReturnUrlAfterLogin } from "@rentiohq/web-shared/dist/scenes/Auth/Auth.utils";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as S from "./ConnectAuthorize.styles";

// INFO: the refusal reason the API sends (MCP_NOT_OFFICE_ADMIN_REASON in rentio-api-next) when
// the person is an administrator of none of their offices.
const MCP_NOT_OFFICE_ADMIN_REASON = "not_office_admin";

// INFO: the connector consent screen. claude.ai sends the user to the API's /oauth/authorize,
// which (when RENTIO_DASHBOARD_URL is set) redirects here. Sign-in reuses the real Rentio
// login: an unauthenticated visitor is sent to /auth/signin and returned here afterwards. On
// approval the dashboard's own session mints the authorization code — the password never
// travels through the connector flow. The layout mirrors the sign-in screen (same frame,
// logo, typography and visual) so the step feels native to Rentio.

export const ConnectAuthorize = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const hasAccessToken = useSelector(authSelectors.hasAccessToken);
  const { user } = authHooks.useSelf();

  const requestId = searchParams.get("requestId") || "";
  const clientName = searchParams.get("client") || "";

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();
  // INFO: set when the API says this person has no access to the Rentio MCP in any of their
  // offices (reason "not_office_admin"). That is a next step, not an error: the screen says
  // who can give access and where, and no longer offers to allow.
  const [hasNoAccess, setHasNoAccess] = React.useState(false);

  const client = clientName || getLocalizedText("connect.authorize.an_app");
  const userName =
    [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
    user?.email ||
    "";

  // INFO: "not you?" — sign out; the not-signed-in effect below then routes through the
  // real Rentio login and returns here, so someone else can approve under their own account.
  const switchAccount = () => {
    dispatch(authActionsV2.signOutStart.getAction());
  };

  // INFO: not signed in -> remember this exact route and send the user through the real
  // Rentio login, which returns here on success (see Auth.utils setReturnUrlAfterLogin).
  React.useEffect(() => {
    if (!hasAccessToken) {
      setReturnUrlAfterLogin(`/connect/authorize${window.location.search}`);
      navigate("/auth/signin", { replace: true });
    }
  }, [hasAccessToken, navigate]);

  const respond = async (decision: "approve" | "decline") => {
    setError(undefined);
    setIsSubmitting(true);
    try {
      const result = await api.post<{ redirectTo: string }>(
        `/oauth/authorize/${decision}`,
        { data: { requestId }, isV2: true, headers: MCP_SOURCE_HEADERS },
      );
      const redirectTo = result?.data?.redirectTo;
      if (redirectTo) {
        window.location.href = redirectTo;
        return;
      }
      setError(getLocalizedText("connect.authorize.failed"));
      setIsSubmitting(false);
    } catch (unknownError) {
      const status = (unknownError as any)?.response?.status;
      switch (status) {
        case 400:
          setError(getLocalizedText("connect.authorize.expired"));
          break;
        case 403:
          if (
            (unknownError as any)?.response?.data?.reason ===
            MCP_NOT_OFFICE_ADMIN_REASON
          ) {
            setHasNoAccess(true);
            break;
          }
          setError(getLocalizedText("connect.authorize.not_allowed"));
          break;
        default:
          setError(getLocalizedText("connect.authorize.failed"));
      }
      setIsSubmitting(false);
    }
  };

  // INFO: while the redirect to sign-in is in flight, render nothing.
  if (!hasAccessToken) {
    return null;
  }

  return (
    <StyledAuthFrame>
      <StyledAuthContent>
        <StyledAuthMain>
          <StyledAuthLogo />
          <StyledAuthBody>
            <DisplayText>
              {getLocalizedText("connect.authorize.title")}
            </DisplayText>
            {requestId ? (
              <>
                <S.Intro>
                  <TextStyle variation="strong">{client}</TextStyle>{" "}
                  {getLocalizedText("connect.authorize.intro")}
                </S.Intro>
                <S.Body>
                  <TextStyle variation="subdued">
                    {getLocalizedText("connect.authorize.body", { client })}
                  </TextStyle>
                </S.Body>
                {userName && (
                  <S.Identity>
                    <TextStyle variation="subdued">
                      {getLocalizedText("connect.authorize.signed_in_as")}{" "}
                      <TextStyle variation="strong">{userName}</TextStyle> —{" "}
                      {getLocalizedText("connect.authorize.not_you")}
                    </TextStyle>{" "}
                    <Button appearance="link" onClick={switchAccount}>
                      {getLocalizedText("connect.authorize.switch_account")}
                    </Button>
                  </S.Identity>
                )}
                {error && (
                  <S.Error>
                    <TextStyle variation="negative">{error}</TextStyle>
                  </S.Error>
                )}
                {hasNoAccess && (
                  <S.Notice>
                    <Banner
                      hasDismiss={false}
                      icon="informationCircle"
                      title={getLocalizedText(
                        "connect.authorize.not_office_admin.title",
                      )}
                      variation="info"
                    >
                      {getLocalizedText("connect.authorize.not_office_admin")}
                    </Banner>
                  </S.Notice>
                )}
                {/* INFO: declining sends the user back to the assistant with access_denied, so
                    its connect flow ends cleanly instead of waiting on this tab. Without access
                    that is the only step left, hence the clearer label. */}
                <S.Actions>
                  {hasNoAccess ? (
                    <Button
                      appearance="primary"
                      onClick={() => respond("decline")}
                      isDisabled={isSubmitting}
                    >
                      {getLocalizedText("connect.authorize.back_to_client", {
                        client,
                      })}
                    </Button>
                  ) : (
                    <>
                      <Button
                        appearance="outline"
                        onClick={() => respond("decline")}
                        isDisabled={isSubmitting}
                      >
                        {getLocalizedText("connect.authorize.deny")}
                      </Button>
                      <Button
                        appearance="primary"
                        onClick={() => respond("approve")}
                        isDisabled={isSubmitting}
                        isSubmitting={isSubmitting}
                      >
                        {getLocalizedText("connect.authorize.allow")}
                      </Button>
                    </>
                  )}
                </S.Actions>
              </>
            ) : (
              <S.Body>
                <TextStyle variation="negative">
                  {getLocalizedText("connect.authorize.expired")}
                </TextStyle>
              </S.Body>
            )}
          </StyledAuthBody>
        </StyledAuthMain>
      </StyledAuthContent>
      <StyledAuthMediaWrap>
        <StyledAuthMedia />
      </StyledAuthMediaWrap>
    </StyledAuthFrame>
  );
};

// eslint-disable-next-line import/no-default-export
export default ConnectAuthorize;
